import React, { useEffect, useRef, useState } from 'react';
import InitialsSymbol from '../../../generic/User/initialsSymbol';
import { SETTING_CONTROL_WIDTH } from './settingRow';

export interface DefaultDropdownItem {
  uuid: string;
  primary: string;
  secondary?: string;
  initials: string;
  color?: string;
}

interface DefaultDropdownProps {
  items: DefaultDropdownItem[];
  /** The entry to show on the toggle, or null for "nothing set". */
  active: DefaultDropdownItem | null;
  onPick: (item: DefaultDropdownItem) => void;
  onClear: () => void;
  noneLabel: string;
  emptyLabel: string;
  /** Temporarily unavailable, e.g. while saving. */
  disabled?: boolean;
  /** Not included in the plan: opens nothing, but stays clickable. */
  locked?: boolean;
}

const OptionRow: React.FC<{
  item: DefaultDropdownItem;
  isActive?: boolean;
}> = ({ item, isActive = false }) => (
  <div className="d-flex align-items-center">
    <InitialsSymbol
      initials={item.initials}
      circle={true}
      color={item.color}
      size={35}
      labelClassName="fs-7"
    />
    <div className="d-flex flex-column ms-3 text-truncate">
      <span className="fw-semibold text-truncate">{item.primary}</span>
      {item.secondary && (
        // On the active row Bootstrap paints the background primary, where
        // text-muted turns unreadable — dim the inherited white instead.
        <span
          className={`fs-8 text-truncate ${
            isActive ? 'opacity-75' : 'text-muted'
          }`}
        >
          {item.secondary}
        </span>
      )}
    </div>
  </div>
);

/**
 * Single-select dropdown showing an avatar, name and role per entry.
 *
 * Store-free by design: the entries, the active one and both callbacks are
 * props, so this renders in Storybook or a test without a slice behind it.
 *
 * Open state is hand-rolled rather than react-bootstrap's <Dropdown>: inside a
 * Modal — which renders into a portal on document.body — @restart/ui's
 * useClickOutside guard (`e === currentEvent`, seeded from window.event) misses,
 * so the very click that opened the menu reached its document listener and
 * closed it again ~4ms later. Listening on mousedown only while open cannot
 * self-close, because the opening mousedown is over by the time this effect
 * runs.
 */
const DefaultDropdown: React.FC<DefaultDropdownProps> = ({
  items,
  active,
  onPick,
  onClear,
  noneLabel,
  emptyLabel,
  disabled = false,
  locked = false
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = (fn: () => void) => {
    fn();
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="position-relative w-100">
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        // A plan-locked control stays clickable on purpose: the click has to
        // reach the upgrade hint wrapper around it. It must not open a menu.
        onClick={() => {
          if (locked) return;
          setOpen((wasOpen) => !wasOpen);
        }}
        className={`btn btn-white btn-sm w-100 d-flex align-items-center justify-content-between text-start border${
          locked ? ' text-muted' : ''
        }`}
      >
        {active ? (
          <span className="d-flex align-items-center text-truncate">
            <InitialsSymbol
              initials={active.initials}
              circle={true}
              color={active.color}
              size={20}
              labelClassName="fs-8"
            />
            <span className="ms-2 text-truncate">{active.primary}</span>
          </span>
        ) : (
          <span className="text-muted">{noneLabel}</span>
        )}
        <i
          className={`ms-2 text-muted fa-solid ${
            locked ? 'fa-lock' : 'fa-angle-down'
          }`}
        ></i>
      </button>

      {open && (
        <div
          className="dropdown-menu show mt-1"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: SETTING_CONTROL_WIDTH,
            maxHeight: '320px',
            overflowY: 'auto'
          }}
        >
          <button
            type="button"
            className={`dropdown-item text-muted${!active ? ' active' : ''}`}
            onClick={() => choose(onClear)}
          >
            {noneLabel}
          </button>
          <div className="dropdown-divider"></div>
          {items.length > 0 ? (
            items.map((item) => (
              <button
                key={item.uuid}
                type="button"
                className={`dropdown-item${
                  item.uuid === active?.uuid ? ' active' : ''
                }`}
                onClick={() => choose(() => onPick(item))}
              >
                <OptionRow item={item} isActive={item.uuid === active?.uuid} />
              </button>
            ))
          ) : (
            <span className="dropdown-item disabled text-muted">
              {emptyLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DefaultDropdown;
